import {
  SkyButton,
  SkyDataGrid,
  SkyDialog,
  SkyDropdown,
  SkyInput,
  SkyMessageBar,
  SkySwitch,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import type { SkyDataGridColumn } from "@skypointcloud/skypoint-ui";
import { useMemo, useState } from "react";
import {
  ROLE_LABELS,
  groupDefaultRolesFor,
  type GroupDefaultRole,
  type RoleName,
} from "@shared/permissions";
import {
  ENFORCEMENT_BLOCK_MESSAGES,
  ENTRA_OBJECT_ID,
  type GroupMappingView,
} from "@shared/types/groupAccess";
import type { MeInstance } from "@shared/types";
import { ReauthDialog } from "@/components/settings/ReauthDialog";
import { EmptyState, ErrorState, LoadingRows } from "@/components/access/ScreenStates";
import {
  useGroupAccessEnabled,
  useGroupAccessMutations,
  useGroupMappings,
  useReauthRetry,
} from "@/hooks/use-group-access";
import { InlineErrorBar, useInlineError, type InlineFailure } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { AddIcon } from "@/lib/icons";
import { LIST_GRID_PAGINATION } from "@/lib/grid";
import { formatInstant, instantHoverTitle } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useResetOnOpen } from "@/hooks/use-reset-on-open";

/**
 * The Users screen's "Directory groups" tab (group access, 2026-09-28; design 4.5).
 *
 * A TenantAdmin maps a group of the tenant's Entra ID to a default role; anyone in it who signs
 * in with a work account is enrolled with that role. "Require a directory group" then makes the
 * groups the only way in for work-account sign-ins, TenantAdmins included. The switch can be
 * turned on only while a group exists and only by someone its rule lets back in; the server
 * enforces both, this screen says why before anyone tries.
 */

/** What each default role does, so the dropdown explains rather than names. */
const GROUP_ROLE_DESCRIPTIONS: Record<GroupDefaultRole, string> = {
  Viewer: "reads connections, schedules and runs",
  Contributor: "edits and runs connections and schedules",
  InstanceAdmin: "runs and configures the instance, and grants access to it",
  TenantAdmin: "manages the tenant's instances and people",
};

const MIN_REASON_LENGTH = 3;

function peopleSentence(count: number): string {
  return count === 1 ? "1 person" : `${count} people`;
}

/**
 * The missing-groups-claim banner (round 4 item 13). It promises "You can add groups now" only
 * while the tenant's directory is set (otherwise `groups-no-directory` names that blocker),
 * and says the switch stays off only while it is off.
 */
function claimMissingMessage(hasDirectory: boolean, enforcing: boolean): string {
  const where =
    "the groups claim is configured on the skyData API app registration (your infrastructure team has the steps in the workforce sign-in setup guide).";
  const lead = hasDirectory
    ? `You can add groups now. People are matched to them once ${where}`
    : `People are matched to groups once ${where}`;
  return enforcing ? lead : `${lead} "Require a directory group" stays off until then.`;
}

const REAUTH_COPY = {
  title: "Confirm your identity to change directory groups",
  description:
    "Your session is still valid, but a change to who is let in by a directory group needs a fresh sign-in.",
  heldMessage:
    "Your change is held. If signing in again takes you to your sign-in page, make the change again when you are back.",
  footnote: "Nothing has changed yet. Cancelling leaves every group as it is.",
};

export function DirectoryGroupsPanel({
  tenantId,
  tenantName,
  grantorRole,
  instances,
}: {
  tenantId: string;
  tenantName: string;
  grantorRole: RoleName;
  /** The tenant's instances the admin can see, for the Instances choice. */
  instances: readonly MeInstance[];
}) {
  const zone = useDisplayTz();
  const enabled = useGroupAccessEnabled();
  const query = useGroupMappings(tenantId, enabled);
  const mutations = useGroupAccessMutations(tenantId);
  const notice = useNotice();
  const { run, hold } = useReauthRetry();
  const [isAddOpen, setAddOpen] = useState(false);
  const [removing, setRemoving] = useState<GroupMappingView | null>(null);
  const [switching, setSwitching] = useState<boolean | null>(null);

  const data = query.data;

  const columns = useMemo<SkyDataGridColumn<GroupMappingView>[]>(
    () => [
      {
        key: "label",
        header: "Group",
        width: "30%",
        renderCell: (_value, mapping) => (
          <div className="flex min-w-0 flex-col">
            <SkyText size={300} weight="medium">
              {mapping.label}
            </SkyText>
            <SkyText size={200} color="muted" className="break-all">
              {mapping.idpGroupId}
            </SkyText>
          </div>
        ),
      },
      {
        key: "roleName",
        header: "Default role",
        width: "14%",
        renderCell: (_value, mapping) => ROLE_LABELS[mapping.roleName],
      },
      {
        key: "instanceName",
        header: "Instances",
        width: "18%",
        renderCell: (_value, mapping) =>
          mapping.scopeType === "tenant"
            ? "Whole tenant"
            : (mapping.instanceName ?? "Other instance"),
      },
      {
        key: "people",
        header: "People",
        width: "10%",
        align: "right",
        renderCell: (_value, mapping) => (
          <SkyText size={200} font="numeric">
            {mapping.people}
          </SkyText>
        ),
      },
      {
        key: "lastSeenAt",
        header: "Last seen",
        width: "16%",
        align: "right",
        renderCell: (_value, mapping) => (
          <SkyText size={200} font="numeric" title={instantHoverTitle(mapping.lastSeenAt, zone)}>
            {mapping.lastSeenAt ? formatInstant(mapping.lastSeenAt, zone, "minutes") : "Not yet"}
          </SkyText>
        ),
      },
      {
        key: "actions",
        header: "Actions",
        width: "12%",
        renderCell: (_value, mapping) => (
          <SkyButton
            appearance="subtle"
            size="small"
            testId={`remove-mapping-${mapping.id}`}
            aria-label={`Remove ${mapping.label} (${mapping.instanceName ?? "whole tenant"})`}
            onClick={() => setRemoving(mapping)}
          >
            Remove
          </SkyButton>
        ),
      },
    ],
    [zone],
  );

  // Group access off (the default; hidden by the owner on 2026-10-01): nothing here, nothing asked.
  if (!enabled) return null;
  if (query.isPending) return <LoadingRows rows={3} columns={6} />;
  if (query.isError || !data) {
    return (
      <ErrorState
        title="The directory groups did not load"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const block = data.caller.enforceBlockedBy;
  const enforcing = data.enforcement.enabled;
  // Off is always allowed; on only when nothing blocks it.
  const switchDisabled = !enforcing && block !== null;

  return (
    <div className="space-y-4" data-testid="directory-groups-panel">
      <div className="flex flex-col gap-2">
        <SkySwitch
          label="Require a directory group"
          checked={enforcing}
          disabled={switchDisabled}
          testId="group-enforcement-switch"
          onBeforeChange={(next) => {
            setSwitching(next);
            return false;
          }}
        />
        <SkyText as="p" size={200} color="muted">
          When on, people from your directory can sign in only while they are in one of these
          groups. Skypoint platform administrators and email-and-password users are not affected.
        </SkyText>
        {switchDisabled && block ? (
          <SkyText as="p" size={200} data-testid="group-enforcement-help">
            {ENFORCEMENT_BLOCK_MESSAGES[block]}
          </SkyText>
        ) : null}
      </div>

      {data.entraTenantId === null ? (
        // Round 4 item 13: the one thing that stops a group being added is the tenant's
        // missing directory (`GroupAccessService.createMappings`), so the tab names it before
        // anyone opens the dialog to find out.
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          testId="groups-no-directory"
          title="Add the tenant's Entra tenant id"
          message="A group is matched only within the tenant's own directory, so adding one waits for the tenant's Microsoft Entra directory (tenant) ID. A Skypoint platform administrator sets it on the Tenants screen."
        />
      ) : null}

      {data.caller.workAccount && data.caller.groupsClaim === "missing" ? (
        // Round 4 item 13: this read like a hard stop. The claim decides only who is matched
        // and whether the switch can go on; adding a mapping never reads anyone's groups.
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          testId="groups-claim-missing"
          title="Your sign-in carries no groups claim"
          message={claimMissingMessage(data.entraTenantId !== null, enforcing)}
        />
      ) : null}

      {data.overageUsers > 0 ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          testId="groups-overage-notice"
          title={`${peopleSentence(data.overageUsers)} have too many groups to read`}
          message="Their sign-in lists more than 200 groups, so it carries none. Assign your group to the skyData enterprise application and choose Groups assigned to the application, or ask Skypoint to turn on the Microsoft Graph lookup."
        />
      ) : null}

      <div className="flex items-center justify-between gap-4">
        <SkyText as="p" size={200} color="muted">
          Only people who sign in with a work account from {tenantName}&apos;s directory are
          matched.
        </SkyText>
        <SkyButton
          appearance="primary"
          icon={AddIcon}
          testId="map-group"
          onClick={() => setAddOpen(true)}
        >
          Add a group
        </SkyButton>
      </div>

      {data.mappings.length === 0 ? (
        <EmptyState
          title="No directory group yet"
          message="Add a group from your Entra ID and choose the role its members get when they sign in."
        />
      ) : (
        <SkyDataGrid<GroupMappingView>
          data={data.mappings}
          columns={columns}
          {...LIST_GRID_PAGINATION}
          getRowId={(mapping) => mapping.id}
          testId="group-mappings-grid"
          emptyMessage="No directory group yet."
        />
      )}

      <AddGroupDialog
        open={isAddOpen}
        onOpenChange={setAddOpen}
        grantorRole={grantorRole}
        instances={instances}
        hasDirectory={data.entraTenantId !== null}
        isSubmitting={mutations.createMapping.isPending}
        onSubmit={async (body, fail) => {
          try {
            const added = () => {
              setAddOpen(false);
              notice.success(`${body.label} can now sign in with ${ROLE_LABELS[body.roleName]}.`, {
                title: "Group added",
              });
            };
            // Null: held for a re-authentication; `added` runs once the replay lands.
            const done = await run(() => mutations.createMapping.mutateAsync(body), added);
            if (done !== null) added();
          } catch (cause) {
            fail(cause);
          }
        }}
      />

      <RemoveGroupDialog
        mapping={removing}
        isSubmitting={mutations.removeMapping.isPending}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        onConfirm={async (mapping, fail) => {
          try {
            const removed = () => {
              setRemoving(null);
              notice.success(`${mapping.label} no longer lets anyone in.`, {
                title: "Group removed",
              });
            };
            const done = await run(
              () => mutations.removeMapping.mutateAsync({ mappingId: mapping.id }),
              removed,
            );
            if (done !== null) removed();
          } catch (cause) {
            fail(cause);
          }
        }}
      />

      <EnforcementDialog
        next={switching}
        outsideGroups={data.outsideGroups}
        isSubmitting={mutations.setEnforcement.isPending}
        onOpenChange={(open) => {
          if (!open) setSwitching(null);
        }}
        onConfirm={async (enabled, reason, fail) => {
          try {
            const switched = () => {
              setSwitching(null);
              notice.success(
                enabled
                  ? "People outside every group can no longer sign in to this tenant."
                  : "Direct grants work again without a group.",
                {
                  title: enabled
                    ? "Directory group required"
                    : "Directory group no longer required",
                },
              );
            };
            const done = await run(
              () => mutations.setEnforcement.mutateAsync({ enabled, reason }),
              switched,
            );
            if (done !== null) switched();
          } catch (cause) {
            fail(cause);
          }
        }}
      />

      <ReauthDialog
        open={hold.isOpen}
        isRetrying={hold.isRetrying}
        error={hold.error}
        onConfirm={hold.confirm}
        onCancel={hold.cancel}
        {...REAUTH_COPY}
        code={hold.code}
        blocked={hold.blocked}
        remember={hold.remember}
      />
    </div>
  );
}

interface AddGroupErrors {
  groupIdError: string | undefined;
  labelError: string | undefined;
  reasonError: string | undefined;
  instancesError: string | undefined;
}

/** Before Add group is pressed, no field says what is wrong with it. */
const NO_ADD_GROUP_ERRORS: AddGroupErrors = {
  groupIdError: undefined,
  labelError: undefined,
  reasonError: undefined,
  instancesError: undefined,
};

/** What each field of the Add group form would say on Add group. */
function addGroupErrors({
  groupId,
  label,
  reason,
  tenantWide,
  instanceIds,
}: {
  groupId: string;
  label: string;
  reason: string;
  tenantWide: boolean;
  instanceIds: readonly string[];
}): AddGroupErrors {
  return {
    groupIdError: groupIdErrorOf(groupId),
    labelError: label.trim() ? undefined : "Name the group as your directory does.",
    reasonError:
      reason.trim().length < MIN_REASON_LENGTH
        ? "Say why. It is recorded on the audit event."
        : undefined,
    instancesError:
      !tenantWide && instanceIds.length === 0 ? "Choose at least one instance." : undefined,
  };
}

function groupIdErrorOf(groupId: string): string | undefined {
  if (!groupId.trim()) return "Enter the group's Object ID from Entra ID.";
  return ENTRA_OBJECT_ID.test(groupId.trim())
    ? undefined
    : "Enter the group's Object ID from Entra ID, a GUID.";
}

/** The Add group form, for a tenant with a directory; `errors` are shown once Add was pressed. */
function AddGroupFields({
  roles,
  instances,
  tenantWide,
  values,
  errors,
  onGroupId,
  onLabel,
  onRoleName,
  onInstanceIds,
  onReason,
  failure,
}: {
  roles: readonly GroupDefaultRole[];
  instances: readonly MeInstance[];
  tenantWide: boolean;
  values: {
    groupId: string;
    label: string;
    roleName: GroupDefaultRole;
    instanceIds: string[];
    reason: string;
  };
  errors: AddGroupErrors;
  onGroupId: (value: string) => void;
  onLabel: (value: string) => void;
  onRoleName: (value: GroupDefaultRole) => void;
  onInstanceIds: (value: string[]) => void;
  onReason: (value: string) => void;
  failure: InlineFailure | null;
}) {
  return (
    <div className="space-y-4">
      <SkyMessageBar
        intent="info"
        layout="multiline"
        message="Only people who sign in with a work account from this tenant's directory are matched. Email-and-password sign-ins keep their direct grants."
      />
      <SkyInput
        label="Group object ID"
        required
        value={values.groupId}
        testId="map-group-id"
        placeholder="00000000-0000-0000-0000-000000000000"
        hint="In Entra ID: Groups, the group, Overview, Object ID."
        validationState={errors.groupIdError ? "error" : "none"}
        validationMessage={errors.groupIdError}
        onChange={(event) => onGroupId(event.target.value)}
      />
      <SkyInput
        label="Label"
        required
        value={values.label}
        testId="map-group-label"
        placeholder="skypoint-skydata"
        validationState={errors.labelError ? "error" : "none"}
        validationMessage={errors.labelError}
        onChange={(event) => onLabel(event.target.value)}
      />
      <SkyDropdown
        label="Default role"
        required
        testId="map-group-role"
        selectedOptions={[values.roleName]}
        options={roles.map((role) => ({
          value: role,
          text: `${ROLE_LABELS[role]}: ${GROUP_ROLE_DESCRIPTIONS[role]}`,
          selectedText: ROLE_LABELS[role],
        }))}
        onOptionSelect={(selected) =>
          onRoleName((selected[0] as GroupDefaultRole) ?? values.roleName)
        }
      />
      {tenantWide ? (
        <SkyText as="p" size={200} color="muted">
          A group with the tenant admin role covers every instance of the tenant.
        </SkyText>
      ) : (
        <SkyDropdown
          label="Instances"
          required
          multiselect
          testId="map-group-instances"
          selectedOptions={values.instanceIds}
          options={instances.map((instance) => ({ value: instance.id, text: instance.name }))}
          validationState={errors.instancesError ? "error" : "none"}
          validationMessage={errors.instancesError}
          onOptionSelect={(selected) => onInstanceIds(selected)}
        />
      )}
      <SkyInput
        label="Reason"
        required
        value={values.reason}
        testId="map-group-reason"
        hint="Recorded on the audit event."
        validationState={errors.reasonError ? "error" : "none"}
        validationMessage={errors.reasonError}
        onChange={(event) => onReason(event.target.value)}
      />
      <InlineErrorBar error={failure} title="The group was not added" testId="map-group-error" />
    </div>
  );
}

function AddGroupDialog({
  open,
  onOpenChange,
  grantorRole,
  instances,
  hasDirectory,
  isSubmitting,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  grantorRole: RoleName;
  instances: readonly MeInstance[];
  hasDirectory: boolean;
  isSubmitting: boolean;
  onSubmit: (
    body: {
      idpGroupId: string;
      label: string;
      roleName: GroupDefaultRole;
      instanceIds?: string[];
      reason: string;
    },
    fail: (cause: unknown) => void,
  ) => void;
}) {
  const roles = useMemo(() => groupDefaultRolesFor(grantorRole), [grantorRole]);
  const error = useInlineError();
  const [groupId, setGroupId] = useState("");
  const [label, setLabel] = useState("");
  const [roleName, setRoleName] = useState<GroupDefaultRole>("Viewer");
  const [instanceIds, setInstanceIds] = useState<string[]>(() =>
    instances.map((i) => i.id).slice(0, 1),
  );
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // Reset during render when the dialog opens, as the grant dialog does.
  useResetOnOpen(open, () => {
    setGroupId("");
    setLabel("");
    setRoleName("Viewer");
    setInstanceIds(instances.map((i) => i.id).slice(0, 1));
    setReason("");
    setSubmitted(false);
    error.clear();
  });

  const tenantWide = roleName === "TenantAdmin";
  const { groupIdError, labelError, reasonError, instancesError } = addGroupErrors({
    groupId,
    label,
    reason,
    tenantWide,
    instanceIds,
  });

  function submit() {
    setSubmitted(true);
    if (!hasDirectory || groupIdError || labelError || reasonError || instancesError) return;
    error.clear();
    onSubmit(
      {
        idpGroupId: groupId.trim(),
        label: label.trim(),
        roleName,
        ...(tenantWide ? {} : { instanceIds }),
        reason: reason.trim(),
      },
      (cause) => error.show(cause, "The group was not added."),
    );
  }

  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      onBeforeClose={(reason) => reason !== "primaryAction"}
      size="medium"
      testId="map-group-dialog"
      title="Add a directory group"
      description="Everyone in the group who signs in gets its role, with nothing typed in by hand."
      primaryAction={{
        label: "Add group",
        onClick: submit,
        loading: isSubmitting,
        disabled: !hasDirectory,
      }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false) }}
    >
      {hasDirectory ? (
        <AddGroupFields
          roles={roles}
          instances={instances}
          tenantWide={tenantWide}
          values={{ groupId, label, roleName, instanceIds, reason }}
          errors={
            submitted
              ? { groupIdError, labelError, reasonError, instancesError }
              : NO_ADD_GROUP_ERRORS
          }
          onGroupId={setGroupId}
          onLabel={setLabel}
          onRoleName={setRoleName}
          onInstanceIds={setInstanceIds}
          onReason={setReason}
          failure={error.error}
        />
      ) : (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          testId="map-group-no-directory"
          title="This tenant has no Entra tenant id"
          message="A group is matched only within the tenant's own directory. A Skypoint platform administrator sets the tenant's Microsoft Entra directory (tenant) ID on the Tenants screen first."
        />
      )}
    </SkyDialog>
  );
}

function RemoveGroupDialog({
  mapping,
  isSubmitting,
  onOpenChange,
  onConfirm,
}: {
  mapping: GroupMappingView | null;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (mapping: GroupMappingView, fail: (cause: unknown) => void) => void;
}) {
  const error = useInlineError();
  return (
    <SkyDialog
      open={mapping !== null}
      onOpenChange={(open) => {
        if (!open) error.clear();
        onOpenChange(open);
      }}
      onBeforeClose={(reason) => reason !== "primaryAction"}
      size="medium"
      testId="remove-mapping-dialog"
      title={mapping ? `Remove ${mapping.label}` : "Remove the group"}
      description={
        mapping
          ? `${peopleSentence(mapping.people)} lose the access this group gave them now, and any role an admin raised them to through it.`
          : ""
      }
      primaryAction={{
        label: "Remove group",
        destructive: true,
        loading: isSubmitting,
        onClick: () => {
          if (mapping)
            onConfirm(mapping, (cause) => error.show(cause, "The group was not removed."));
        },
      }}
      secondaryAction={{ label: "Keep the group", onClick: () => onOpenChange(false) }}
    >
      <InlineErrorBar
        error={error.error}
        title="The group was not removed"
        testId="remove-mapping-error"
      />
    </SkyDialog>
  );
}

function EnforcementDialog({
  next,
  outsideGroups,
  isSubmitting,
  onOpenChange,
  onConfirm,
}: {
  /** The value the switch is being moved to, or null when closed. */
  next: boolean | null;
  outsideGroups: number;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (enabled: boolean, reason: string, fail: (cause: unknown) => void) => void;
}) {
  const error = useInlineError();
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [shownFor, setShownFor] = useState<boolean | null>(next);
  if (shownFor !== next) {
    setShownFor(next);
    setReason("");
    setSubmitted(false);
    error.clear();
  }
  const reasonError =
    reason.trim().length < MIN_REASON_LENGTH
      ? "Say why. It is recorded on the audit event."
      : undefined;
  const turningOn = next === true;

  function submit() {
    setSubmitted(true);
    if (next === null || reasonError) return;
    onConfirm(next, reason.trim(), (cause) => error.show(cause, "The change was not saved."));
  }

  return (
    <SkyDialog
      open={next !== null}
      onOpenChange={onOpenChange}
      onBeforeClose={(closeReason) => closeReason !== "primaryAction"}
      size="medium"
      testId="group-enforcement-dialog"
      title={turningOn ? "Require a directory group" : "Stop requiring a directory group"}
      description={
        turningOn
          ? "From now on a person who signs in with a work account reaches this tenant only while they are in one of its groups, tenant admins included."
          : "Direct grants work again for people outside every group."
      }
      primaryAction={{
        label: turningOn ? "Turn on" : "Turn off",
        onClick: submit,
        loading: isSubmitting,
        destructive: turningOn,
      }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false) }}
    >
      <div className="space-y-4">
        {turningOn ? (
          <SkyMessageBar
            intent={outsideGroups > 0 ? "warning" : "info"}
            layout="multiline"
            testId="group-enforcement-outside"
            message={`${peopleSentence(outsideGroups)} with access in this tenant ${outsideGroups === 1 ? "is" : "are"} in none of its groups today. They cannot sign in once this is on, until they are added to a group.`}
          />
        ) : null}
        <SkyInput
          label="Reason"
          required
          value={reason}
          testId="group-enforcement-reason"
          hint="Recorded on the audit event."
          validationState={submitted && reasonError ? "error" : "none"}
          validationMessage={submitted ? reasonError : undefined}
          onChange={(event) => setReason(event.target.value)}
        />
        <InlineErrorBar
          error={error.error}
          title="The change was not saved"
          testId="group-enforcement-error"
        />
      </div>
    </SkyDialog>
  );
}
